import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  Button,
  Card,
  Content as CardContent,
  Text as CardDescription,
  Header as CardHeader,
  Heading as CardTitle,
} from '@react-spectrum/s2'
import { CheckCircle2, Home, Settings } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
/**
 * Checkout Success Page
 *
 * This page is displayed after a successful payment.
 * It confirms the subscription activation and redirects to the dashboard.
 */
import { getPlanSummary } from '@rpc/plans.rpc'
import { getSubscriptionStatus } from '@rpc/billing.rpc'
import { trackAnalyticsEvent } from '@client/analytics'
import { APP_ROUTES } from '@shared/app-routes'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { fullWidthFlexButtonStyles, fullWidthStyles } from '@/styles/s2-macros'

const checkoutSuccessSearchSchema = z.object({
  planId: z.string().optional(),
})

const successRoute = createFileRoute('/checkout/success')({
  validateSearch: (search) => checkoutSuccessSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    planId: search.planId ?? 'premiumUser',
  }),
  loader: async ({ deps: { planId } }) => {
    return {
      plan: await getPlanSummary({ data: { planId } }),
    }
  },
  component: CheckoutSuccessPage,
})

function CheckoutSuccessPage() {
  const { plan } = successRoute.useLoaderData()
  const navigate = useNavigate()
  const [activationState, setActivationState] = useState<
    'checking' | 'active' | 'pending' | 'error'
  >('checking')
  const [activationError, setActivationError] = useState<string | null>(null)
  const reportedActivationState = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const expectedPlanId = plan.id
    const MAX_ATTEMPTS = 8
    const POLL_INTERVAL_MS = 1500

    const checkStatus = async (attempt: number) => {
      try {
        const status = await getSubscriptionStatus()
        const isActivated =
          status.hasSubscription &&
          status.planId === expectedPlanId &&
          (status.status === 'active' || status.status === 'trialing')

        if (cancelled) return

        if (isActivated) {
          setActivationState('active')
          setActivationError(null)
          return
        }

        if (attempt >= MAX_ATTEMPTS) {
          setActivationState('pending')
          return
        }

        timeoutId = setTimeout(() => {
          void checkStatus(attempt + 1)
        }, POLL_INTERVAL_MS)
      } catch (error) {
        if (cancelled) return
        setActivationState('error')
        setActivationError(
          error instanceof Error
            ? error.message
            : 'We could not confirm your subscription yet.',
        )
      }
    }

    void checkStatus(1)

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [plan.id])

  useEffect(() => {
    if (activationState === 'checking') return
    if (reportedActivationState.current === activationState) return
    reportedActivationState.current = activationState
    trackAnalyticsEvent('checkout_activation_resolved', {
      plan_id: plan.id,
      state: activationState,
    })
  }, [activationState, plan.id])

  const title = useMemo(() => {
    if (activationState === 'active') {
      return `Welcome to ${plan.name}!`
    }
    if (activationState === 'error') {
      return 'Payment received'
    }
    return 'Finalising your subscription'
  }, [activationState, plan.name])

  const description = useMemo(() => {
    if (activationState === 'active') {
      return 'Your subscription has been activated successfully.'
    }
    if (activationState === 'error') {
      return 'We have not confirmed activation yet. Please refresh shortly or check your dashboard.'
    }
    return 'We are waiting for billing confirmation before turning on your upgraded access.'
  }, [activationState])

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <Header />
      <main
        id="main-content"
        className="flex-1 flex items-center justify-center p-4"
      >
        <div className="w-full max-w-lg">
          <Card styles={fullWidthStyles}>
            <CardHeader>
              <div className="space-y-4 text-center">
                <div className="flex justify-center">
                  <div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                    <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-500" />
                  </div>
                </div>
                <CardTitle>
                  <span className="text-2xl">{title}</span>
                </CardTitle>
                <CardDescription>{description}</CardDescription>
              </div>
            </CardHeader>

            <CardContent>
              <div className="space-y-6">
                {/* Plan Details */}
                <div className="bg-neutral-100 dark:bg-neutral-800 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral-500">Plan</span>
                    <span className="font-semibold">{plan.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral-500">Price</span>
                    <span className="font-semibold">{plan.price}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral-500">Status</span>
                    <span className="font-semibold text-green-600 dark:text-green-500">
                      {activationState === 'active'
                        ? 'Active'
                        : activationState === 'error'
                          ? 'Pending confirmation'
                          : 'Activating'}
                    </span>
                  </div>
                </div>

                {/* What's Next */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm">What's next?</h3>
                  {activationState === 'active' ? (
                    <ul className="space-y-2 text-sm text-neutral-600 dark:text-neutral-400">
                      <li>• Explore all premium features</li>
                      <li>• Enjoy increased query limits</li>
                      <li>• Access broader planning guidance and tools</li>
                    </ul>
                  ) : (
                    <ul className="space-y-2 text-sm text-neutral-600 dark:text-neutral-400">
                      <li>
                        • Keep this page open while billing confirmation
                        completes
                      </li>
                      <li>
                        • Your upgraded limits will appear as soon as activation
                        finishes
                      </li>
                      <li>
                        • If this takes longer than expected, refresh or check
                        the dashboard
                      </li>
                    </ul>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {activationState === 'active' ? (
                    <>
                      <Button
                        styles={fullWidthFlexButtonStyles}
                        variant="primary"
                        onPress={() => {
                          trackAnalyticsEvent(
                            'checkout_success_action_clicked',
                            {
                              action: 'dashboard',
                              activation_state: activationState,
                            },
                          )
                          navigate({ to: APP_ROUTES.dashboard.home })
                        }}
                      >
                        <Home className="mr-2 h-4 w-4" />
                        Go to Dashboard
                      </Button>
                      <Button
                        styles={fullWidthFlexButtonStyles}
                        variant="secondary"
                        fillStyle="outline"
                        onPress={() => {
                          trackAnalyticsEvent(
                            'checkout_success_action_clicked',
                            {
                              action: 'manage_subscription',
                              activation_state: activationState,
                            },
                          )
                          navigate({ to: APP_ROUTES.dashboard.subscription })
                        }}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Manage subscription
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        styles={fullWidthFlexButtonStyles}
                        variant="primary"
                        onPress={() => {
                          trackAnalyticsEvent(
                            'checkout_success_action_clicked',
                            {
                              action: 'check_again',
                              activation_state: activationState,
                            },
                          )
                          window.location.reload()
                        }}
                      >
                        Check again
                      </Button>
                      <Button
                        styles={fullWidthFlexButtonStyles}
                        variant="secondary"
                        fillStyle="outline"
                        onPress={() => {
                          trackAnalyticsEvent(
                            'checkout_success_action_clicked',
                            {
                              action: 'dashboard',
                              activation_state: activationState,
                            },
                          )
                          navigate({ to: APP_ROUTES.dashboard.home })
                        }}
                      >
                        <Home className="mr-2 h-4 w-4" />
                        Go to Dashboard
                      </Button>
                    </>
                  )}
                </div>

                <p className="text-xs text-center text-neutral-500">
                  {activationState === 'active'
                    ? 'A confirmation email has been sent to your registered email address.'
                    : (activationError ??
                      'We will show the upgraded access here as soon as the payment webhook completes.')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  )
}
export { successRoute as Route }
